import React, { useState, useCallback } from 'react'; // Dodano useCallback
import { useParams, useNavigate } from 'react-router-dom';
import { Send, Loader2, AlertCircle, CheckCircle, ExternalLink } from 'lucide-react';
import './PublicAiOrderForm.css';

// Stałe dla odpytywania
const POLLING_INTERVAL = 5000; // 5 sekund
const MAX_POLLING_ATTEMPTS = 120; // 5s * 120 = 600s = 10 minut

function PublicAiOrderForm({ backendUrl }) {
  const { docnumber } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [projectInfo, setProjectInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [orderPublicKey, setOrderPublicKey] = useState(null);
  const [pollingMessage, setPollingMessage] = useState(''); // Stan dla komunikatu odpytywania

  const pollInteractionStatus = useCallback(async (interactionId) => {
    let attempts = 0;
    const poll = async (resolve, reject) => {
      if (attempts >= MAX_POLLING_ATTEMPTS) {
        reject(new Error("Przekroczono limit czasu oczekiwania na odpowiedź serwera."));
        return;
      }
      attempts++;
      setPollingMessage(`Sprawdzanie statusu (${attempts}/${MAX_POLLING_ATTEMPTS})...`);

      try {
        const statusUrl = `${backendUrl}/public/ai-interaction-status/${interactionId}`;
        const statusRes = await fetch(statusUrl);

        if (!statusRes.ok && statusRes.status !== 404) {
          const errorText = await statusRes.text();
          console.error(`[pollInteractionStatus] Błąd HTTP ${statusRes.status}: ${errorText}`);
          setTimeout(() => poll(resolve, reject), POLLING_INTERVAL);
          return;
        }
        
        const statusData = await statusRes.json();
        console.log(`[pollInteractionStatus] Attempt ${attempts}, Status:`, statusData);

        if (statusData.status === 'completed_success') {
          // Zawsze rozwiązuj z całym obiektem statusu, aby uniknąć błędów
          resolve(statusData);
        } else if (statusData.status === 'completed_error') {
          reject(new Error(statusData.data || "Wystąpił błąd po stronie serwera."));
        } else {
          setTimeout(() => poll(resolve, reject), POLLING_INTERVAL);
        }
      } catch (pollError) {
        console.error("[pollInteractionStatus] Błąd podczas odpytywania:", pollError);
        setTimeout(() => poll(resolve, reject), POLLING_INTERVAL);
      }
    };
    return new Promise(poll);
  }, [backendUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setPollingMessage('');

    if (!email || !email.includes('@')) {
      setError('Proszę podać poprawny adres email.');
      return;
    }
    if (!projectInfo.trim()) {
      setError('Proszę podać informacje dotyczące projektu.');
      return;
    }

    setIsLoading(true);
    const clientInteractionId = `client-public-${Date.now()}`;

    try {
      const response = await fetch(`${backendUrl}/public/ai-order-from-template/${docnumber}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientEmail: email,
          projectInfo: projectInfo,
          clientInteractionId: clientInteractionId,
        }),
      });

      const result = await response.json();

      if (response.status !== 202 || !result.interactionId) {
        throw new Error(result.message || 'Nie udało się rozpocząć przetwarzania AI.');
      }

      setPollingMessage('Serwer przyjął żądanie. Oczekiwanie na ukończenie...');
      const pollingResult = await pollInteractionStatus(result.interactionId);

      setSuccess(true);
      // Ostateczna, poprawna wersja odczytu klucza bezpośrednio z obiektu pollingResult
      if (pollingResult && pollingResult.orderPublicKey) {
        setOrderPublicKey(pollingResult.orderPublicKey);
      } else {
        console.error("Nie znaleziono 'orderPublicKey' w odpowiedzi statusu:", pollingResult);
        throw new Error("Nie udało się uzyskać linku do zamówienia po przetworzeniu.");
      }
      
    } catch (err) {
      console.error("Błąd podczas wysyłania formularza AI:", err);
      setError(err.message || 'Nie udało się połączyć z serwerem.');
    } finally {
      setIsLoading(false);
      setPollingMessage('');
    }
  };

  return (
    <div className="ai-order-form-container">
      <h1>W ciągu 5 min przygotujemy dla Ciebie projekt</h1>
      {/* Usunięto podtytuł */}

      {isLoading ? (
        <div className="ai-order-message info">
          <Loader2 size={20} className="animate-spin" />
          <div>
            <span>Tworzymy Twój projekt...</span>
            <p className="small-info">
              Może to potrwać kilka minut. Możesz bezpiecznie zamknąć tę stronę.
              Gdy projekt będzie gotowy, wyślemy Ci e-mail z linkiem na adres {email}.
            </p>
            {pollingMessage && (
              <p className="small-info" style={{ marginTop: '10px', fontWeight: 'bold' }}>
                {pollingMessage}
              </p>
            )}
          </div>
        </div>
      ) : success ? (
        <div className="ai-order-message success">
          {/* Usunięto logowanie stanu */}
          <CheckCircle size={20} />
          <div>
            <span>Projekt został wygenerowany pomyślnie!</span>
            <p className="small-info">Link do panelu zamówienia został również wysłany na Twój adres e-mail ({email}). Możesz teraz przejść do panelu, aby zobaczyć i edytować swój projekt.</p>
      {console.log("Rendering success message. orderPublicKey:", orderPublicKey)}
      {orderPublicKey && (
        <button
          onClick={() => navigate(`/client/orders/${orderPublicKey}`)}
          className="client-btn ai-order-form-button" // Dodajmy specyficzną klasę dla ewentualnych styli i zachowajmy .client-btn
          style={{ marginTop: '15px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '10px 15px' }} // Dodatkowe style dla pewności
        >
          {/* Możemy usunąć ikonę ExternalLink jeśli nie pasuje, lub zastąpić inną */}
          {/* <ExternalLink size={18} style={{ marginRight: '8px' }} /> */}
          Przejdź do panelu zamówienia
        </button>
      )}
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="ai-order-form">
           {/* Komunikat o błędzie przeniesiony nad pola formularza dla lepszej widoczności */}
           {error && (
            <div className="ai-order-message error">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}
          <div className="form-group">
            <label htmlFor="email">Twój adres email:</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="np. jan.kowalski@example.com"
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="projectInfo">Informacje do projektu:</label>
            <textarea
              id="projectInfo"
              value={projectInfo}
              onChange={(e) => setProjectInfo(e.target.value)}
              required
              rows={8}
              placeholder="Podaj nam jak najwięcej danych, np. Imiona Pary Młodej, data, miejsce, godzina lub jaki napis ma się znaleźć na projekcie."
              disabled={isLoading}
            />
          </div>

          {/* Usunięto komunikat o błędzie stąd, przeniesiony wyżej */}

          <button type="submit" disabled={isLoading} className="submit-button">
            {/* Stan ładowania jest teraz obsługiwany przez blok warunkowy wyżej */}
            <>
              <Send size={18} />
              <span>Stwórz projekt i wyślij mi link</span>
              </>
          </button>
        </form>
      )}
    </div>
  );
}

export default PublicAiOrderForm;
